/**
 * Helpers for new lead, sales and traffic campaigns with one campaign budget that Meta shares across the ad sets,
 * with an optional daily minimum and maximum spend per ad set.
 *
 * Wizard state used here:
 *   budgetSchedule.budgetType       'campaign_budget' in this mode
 *   budgetSchedule.campaignBudget   daily campaign budget as typed (major units)
 *   budgetSchedule.adSetLimits      { '<stage>_<audience>': { min: '<typed>', max: '<typed>' } }, empty by default
 *
 * Amounts are typed in major units and sent in minor units: the app stores every currency times 100, also the ones
 * without decimals, so 5000 JPY is sent as 500000. The server offers the mode (licence switch, supported objective, no
 * special ad categories, known account minimum) and checks the budget and the limits again when it saves them.
 */
import { maximumToMinor } from '@/utils/pixel-warmup'
import { isZeroDecimalCurrency } from '@/utils/tools'
import {
    isAdvantageLeadsBudget,
    campaignBudgetForSubmission as advantageLeadsSubmission,
} from '@/utils/advantage-leads'

export const CAMPAIGN_BUDGET_TYPE = 'campaign_budget'

export const CAMPAIGN_BUDGET_LABEL = 'Campaign budget'

export const CAMPAIGN_BUDGET_DESCRIPTION =
    'One daily budget for the whole campaign. Meta shares it across the audiences, and you can set a minimum and a maximum daily spend for each audience.'

export const CAMPAIGN_BUDGET_SPLIT_NOTE =
    'Meta moves this campaign budget towards the audiences that perform, within the limits you set, so the results of the audiences are not a fair side-by-side comparison.'

export const CAMPAIGN_BUDGET_LIMITS_NOTE =
    'Limits are optional. A minimum is a target Meta tries to reach every day, not a guarantee. A maximum is the most the audience can spend in a day.'

export const CAMPAIGN_BUDGET_AD_SET_NOTE = 'From the campaign budget'

export const CAMPAIGN_BUDGET_EXISTING_NOTE =
    'This campaign has one campaign budget. The new audiences share it with the other audiences, so they get no budget of their own. You can set a minimum and a maximum daily spend for each new audience. Change the campaign budget in Ads Manager if needed.'

/** Largest amount an integer budget column holds, in minor units. */
export const MAX_STORED_MINOR = 2147483647

export const CAMPAIGN_BUDGET_ERROR_MESSAGES = {
    CAMPAIGN_BUDGET_NOT_AVAILABLE:
        'Campaign budgets are not available for this campaign any more. Choose another budget type.',
    INVALID_CAMPAIGN_BUDGET: 'Check the campaign budget: it must cover the minimum daily budget of every audience.',
    INVALID_SPEND_LIMITS:
        'Check the daily spend limits: a maximum cannot be below its minimum, and the minimums cannot add up to more than the campaign budget.',
    CAMPAIGN_BUDGET_GOAL:
        'All audiences of a campaign with a campaign budget need the same optimization goal. Lead audiences with an instant form and without one cannot share a campaign budget.',
    SPEND_LIMITS_CHANGED:
        'The spend limits of this audience changed since you opened the edit, so they were not changed. Open the edit again to see the current limits.',
}

/**
 * Message for a launch or edit error caused by the campaign budget or its limits, or null for any other error. The
 * server words its refusals in plain text (an unsupported currency, an unknown account minimum, a maximum below the
 * account minimum, ...), so that text is shown; the fixed text is only the fallback for a refusal without one.
 */
export const getCampaignBudgetErrorMessage = (error) => {
    const first = error?.graphQLErrors?.[0]
    const fallback = CAMPAIGN_BUDGET_ERROR_MESSAGES[first?.extensions?.code]
    if (!fallback) return null
    const message = typeof first.message === 'string' ? first.message.trim() : ''
    return message || fallback
}

export const isPlainCampaignBudget = (budgetData) => budgetData?.budgetType === CAMPAIGN_BUDGET_TYPE

/** Whether the budget step uses one campaign budget of either kind (Advantage+ leads or the plain campaign budget). */
export const hasCampaignBudget = (budgetData) => isAdvantageLeadsBudget(budgetData) || isPlainCampaignBudget(budgetData)

/** Key of an audience (future ad set) in the limits: the same as the edit wizard's ad set key. */
export const limitKey = (stage, audienceId) => `${stage}_${audienceId}`

/**
 * One typed limit in minor units. Strict: digits with an optional decimal part and nothing else ("1,5", "100abc",
 * "0x10", "1e3" and a zero are refused). An empty box is no limit. With a `currency` that has no decimals a decimal
 * part is refused too: Meta takes whole amounts there.
 * @returns {{ state: 'empty' | 'set' | 'invalid' | 'not_whole' | 'too_large', minor: number | null }}
 */
export const limitToMinor = (typed, currency) => {
    const text = String(typed ?? '').trim()
    if (text === '') return { state: 'empty', minor: null }
    const minor = maximumToMinor(text)
    if (minor === null) return { state: 'invalid', minor: null }
    if (currency && isZeroDecimalCurrency(currency) && text.includes('.')) return { state: 'not_whole', minor: null }
    if (minor > MAX_STORED_MINOR) return { state: 'too_large', minor: null }
    return { state: 'set', minor }
}

const amountProblem = (what, name, parsed, currency) => {
    if (parsed.state === 'not_whole') {
        return `Enter the ${what} of ${name} as a whole amount: ${currency} has no decimals.`
    }
    if (parsed.state === 'invalid') {
        return `Enter the ${what} of ${name} as a number greater than zero, for example 20 or 20.50, or leave it empty.`
    }
    if (parsed.state === 'too_large') return `The ${what} of ${name} is too large.`
    return null
}

/**
 * Why the campaign budget cannot be used, or null. `amount` is as typed (major units); `minDailyBudget` is the ad
 * account minimum per ad set in minor units. Every ad set must still be able to get the minimum.
 * @returns {string | { minimum: number } | null}
 */
export const plainCampaignBudgetProblem = (amount, adSetCount, minDailyBudget, currency) => {
    const text = String(amount ?? '').trim()
    if (text === '') return 'Enter the daily campaign budget.'
    const minor = maximumToMinor(text)
    if (minor === null) return 'Enter the daily campaign budget as a number greater than zero, for example 60 or 60.50.'
    if (limitToMinor(text, currency).state === 'not_whole') {
        return `Enter the daily campaign budget as a whole amount: ${currency} has no decimals.`
    }
    if (minor > MAX_STORED_MINOR) return 'This campaign budget is too large.'
    const count = Math.max(Number(adSetCount) || 0, 1)
    const minimum = (Number(minDailyBudget) || 0) * count
    if (minimum > 0 && minor < minimum) return { minimum }
    return null
}

/**
 * Why the limits of the audiences cannot be saved, or null. `adSets` lists `{ key, name }`; `limits` holds what was typed
 * by key. The server checks the same rules again and its refusal is shown as it is.
 * - each limit is empty or a positive amount;
 * - a maximum is not below its own minimum and not below the minimum daily budget of the ad account;
 * - the minimums do not add up to more than the campaign budget.
 * @param {{ campaignBudget?: string, minDailyBudget?: number, adSets: Array<{key: string, name?: string}>, limits?: object, currency?: string, formatAmount?: (minor: number) => string }} params
 */
export const describeLimitsProblem = ({
    campaignBudget,
    minDailyBudget = 0,
    adSets,
    limits = {},
    currency,
    formatAmount = (minor) => String(minor / 100),
}) => {
    let minimums = 0
    for (const { key, name } of adSets) {
        const label = name ? `"${name}"` : 'an audience'
        const min = limitToMinor(limits[key]?.min, currency)
        const max = limitToMinor(limits[key]?.max, currency)
        const found =
            amountProblem('minimum daily spend', label, min, currency) ||
            amountProblem('maximum daily spend', label, max, currency)
        if (found) return found
        if (min.minor !== null && max.minor !== null && max.minor < min.minor) {
            return `The maximum daily spend of ${label} cannot be below its minimum.`
        }
        if (max.minor !== null && Number(minDailyBudget) > 0 && max.minor < Number(minDailyBudget)) {
            return `The maximum daily spend of ${label} must be at least ${formatAmount(
                Number(minDailyBudget),
            )}, the minimum daily budget of your ad account.`
        }
        minimums += min.minor || 0
    }
    const budget = maximumToMinor(campaignBudget)
    if (budget !== null && minimums > budget) {
        return 'The minimum daily spends of the audiences cannot add up to more than the campaign budget.'
    }
    return null
}

/** The limits to send with the audiences, in minor units, by key; only the limits that are set. */
export const limitsForSubmission = (budgetData) => {
    const typed = budgetData?.adSetLimits || {}
    const sent = {}
    for (const [key, entry] of Object.entries(typed)) {
        const min = limitToMinor(entry?.min)
        const max = limitToMinor(entry?.max)
        const fields = {
            ...(min.state === 'set' ? { min_spend_target: min.minor } : {}),
            ...(max.state === 'set' ? { spend_cap: max.minor } : {}),
        }
        if (Object.keys(fields).length > 0) sent[key] = fields
    }
    return sent
}

/** The campaign budget fields of a new campaign (either kind), or null with ad set budgets. */
export const anyCampaignBudgetForSubmission = (budgetData) => {
    if (isPlainCampaignBudget(budgetData)) {
        const minor = maximumToMinor(budgetData.campaignBudget)
        return { budget_mode: CAMPAIGN_BUDGET_TYPE, daily_budget: minor === null ? 0 : minor }
    }
    return advantageLeadsSubmission(budgetData)
}

/**
 * Limit fields of one ad set in an edit (minor units): nothing for a limit that is the one the edit was opened with, so
 * a limit changed elsewhere meanwhile is not set back. A changed limit is always sent with the opened one to compare
 * against (null when it was empty); an emptied limit is sent as null. `opened` is `{ min, max }` in minor units (null for none).
 */
export const spendLimitEditFields = (typed, opened) => {
    const fields = {}
    const pairs = [
        ['min_spend_target', 'expected_min_spend_target', limitToMinor(typed?.min), opened?.min],
        ['spend_cap', 'expected_spend_cap', limitToMinor(typed?.max), opened?.max],
    ]
    for (const [field, expectedField, parsed, openedValue] of pairs) {
        if (parsed.state === 'invalid' || parsed.state === 'too_large') continue
        const next = parsed.minor
        const before = Number.isFinite(openedValue) ? openedValue : null
        if (next === before) continue
        // The server needs the limit the form was opened with for every change, null for a limit that was empty
        fields[field] = next
        fields[expectedField] = before
    }
    return fields
}

/** Typed form of limits stored in minor units, for the wizard's step data. */
export const typedLimitsOf = (minSpendTarget, spendCap) => ({
    min: minSpendTarget != null ? String(Number(minSpendTarget) / 100) : '',
    max: spendCap != null ? String(Number(spendCap) / 100) : '',
})

/** Budget step data for a campaign edited or duplicated from one with a plain campaign budget (minor units stored). */
export const plainCampaignBudgetStepData = (dailyBudget, adSetLimits = {}) => ({
    budgetType: CAMPAIGN_BUDGET_TYPE,
    campaignBudget: dailyBudget != null ? String(Number(dailyBudget) / 100) : '',
    adSetLimits,
})

/**
 * Part of the campaign budget the AI audiences can count on (minor units): the campaign budget less what every other
 * audience is held to at least (the larger of the ad account minimum and its own minimum spend), but never less than
 * the minimum spends the AI audiences have of their own. `others` and `ai` list `{ minimum }` (minor units, 0 for none).
 */
export const plainCampaignBudgetShare = ({ campaignBudget, minDailyBudget, others = [], ai = [] }) => {
    const floorOfOthers = others.reduce(
        (sum, { minimum }) => sum + Math.max(Number(minimum) || 0, Number(minDailyBudget) || 0),
        0,
    )
    const ownMinimums = ai.reduce((sum, { minimum }) => sum + (Number(minimum) || 0), 0)
    return Math.max(Math.max(Number(campaignBudget) || 0, 0) - floorOfOthers, ownMinimums, 0)
}

/** The daily spend limits of an Ads Manager ad set as one line, or '' for none. `formatAmount` takes minor units. */
export const spendLimitsText = (adSet, formatAmount) => {
    const parts = [
        adSet?.min_spend_target != null ? `Minimum ${formatAmount(adSet.min_spend_target)} a day` : null,
        adSet?.spend_cap != null ? `Maximum ${formatAmount(adSet.spend_cap)} a day` : null,
    ].filter(Boolean)
    return parts.join(', ')
}
